<template>
    <modal size="lg" @close="reset(); $emit('close')" :height-max="true" ref="modal" :is-closeable="isCloseable" :autoshow="true">
        <template v-slot:title>
            <h3 class="modal-title">Add Deferred Compensation Plan</h3>
        </template>

        <slot>
            <div v-show="step == 'plan'">
                <plan-form
                    ref="planForm"
                    :initial-value="formData.plan"
                />
            </div>

            <div v-show="step == 'match'">
                <employer-match-form
                    ref="employerMatchForm"
                    :initial-value="formData.match"
                />
            </div>

            <div v-if="step == 'plan-numbers'">
                <plan-numbers-form
                    ref="planNumbersForm"
                    :initial-value="formData.plan_numbers"
                    :client="client"
                    :plans="plans"
                    :plan-type="formData.plan.plan_type"
                    :starts-on="formData.plan.starts_on"
                />
            </div>

            <div v-if="step == 'positions' && positionsLoaded">
                <position-selector
                    :all-positions="allCompanyPositions"
                    :companies="client.companies"
                    v-model="formData.positions"
                />
            </div>

            <div v-if="step == 'contribution-basis'">
                <contribution-basis-form
                    ref="contributionBasisForm"
                    :initial-value="formData.company_earning_types"
                    :client="client"
                    :plan-numbers="formData.plan_numbers"
                />
            </div>
        </slot>

        <template v-slot:footer>
            <form-errors :errors="formErrors"/>

            <button-row stacked>
                <button v-if="step == 'plan'" type="button" class="btn btn-outline-primary" @click.prevent="close()">
                    Cancel
                </button>
                <button v-if="['match', 'plan-numbers', 'positions', 'contribution-basis'].includes(step)" type="button" class="btn btn-outline-primary" @click.prevent="previousStep">
                    Back
                </button>
                <button v-if="['plan', 'match', 'plan-numbers', 'positions'].includes(step)" type="button" class="btn btn-primary" @click.prevent="nextStep">
                    Next
                </button>

                <button v-if="step == 'contribution-basis'" type="button" class="btn btn-primary" @click.prevent="onSubmit()">
                    Save
                </button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'
import PlanForm from './PlanForm'
import PlanNumbersForm from './PlanNumbersForm'
import EmployerMatchForm from './EmployerMatchForm'
import ContributionBasisForm from './ContributionBasisForm'
import CompanySelector from '@/pages/client/Settings/components/CompanySelector'
import PositionSelector from '@/pages/client/Settings/components/PositionSelector'

export default {
    props: ['client', 'plans', ],
    mixins: [FormMixin, ModalMixin],
    components: {PlanForm, CompanySelector, PlanNumbersForm, EmployerMatchForm, ContributionBasisForm, PositionSelector, },
    emits: ['updated', 'close', ],
    mounted() {
        this.getPositions()
    },
    data() {
        return {
            formData: this.makeFormData(),
            step: 'plan',
            positionsLoaded: false,
            allPositions: [],
        }
    },
    computed: {
        allCompanyPositions() {
            const companies = Array.from(Object.keys(this.formData.plan_numbers))
            return this.allPositions.filter(p => companies.includes('' + p.company_id))
        },
    },
    methods: {
        makeFormData() {
            return {
                plan: {
                    provider_name: '',
                },
                match: {
                    has_employer_match: '',
                    match_tiers: [],
                },
                plan_numbers: {},
                positions: [],
                company_earning_types: {},
            }
        },
        previousStep() {
            if (this.step == 'match') {
                this.step = 'plan'
            }
            if (this.step == 'plan-numbers') {
                this.formData.match = this.$refs.employerMatchForm.getFormData()
                this.step = 'match'
            }
            if (this.step == 'positions') {
                this.step = 'plan-numbers'
            }
            if (this.step == 'contribution-basis') {
                if (this.$refs.contributionBasisForm.goBack()) {
                    return  // Was drilled into one company; returned to the company list
                }

                // Preserve what has been configured so far, since the step unmounts.
                this.formData.company_earning_types = this.$refs.contributionBasisForm.getFormData()
                this.step = 'positions'
            }
        },
        nextStep() {
            this.formErrors = []

            if (this.step == 'plan') {
                if (!this.$refs.planForm.validate()) {
                    this.formErrors = this.$refs.planForm.formErrors
                    return
                }

                this.formData.plan = this.$refs.planForm.getFormData()
                this.step = 'match'
            }
            else if (this.step == 'match') {
                if (!this.$refs.employerMatchForm.validate()) {
                    this.formErrors = this.$refs.employerMatchForm.formErrors
                    return
                }

                this.formData.match = this.$refs.employerMatchForm.getFormData()
                this.step = 'plan-numbers'
            }
            else if (this.step == 'plan-numbers') {
                if (!this.$refs.planNumbersForm.validate()) {
                    this.formErrors = this.$refs.planNumbersForm.formErrors
                    return
                }

                this.formData.plan_numbers = this.$refs.planNumbersForm.getPlanNumbers()  // NB this does use getPlanNumbers() and not getFormData()
                this.step = 'positions'
            }
            else if (this.step == 'positions') {
                this.step = 'contribution-basis'
            }
        },
        onSubmit() {
            if (!this.validate()) {
                return
            }

            this.formData.company_earning_types = this.$refs.contributionBasisForm.getFormData()

            this.$store.dispatch('START_LOADING')
            const data = this.getFormData()

            // The employer match is its own step, and the API takes both parts of it at
            // the top level of the payload.
            data.has_employer_match = data.match.has_employer_match
            data.match_tiers = data.match.match_tiers
            const url = `/clients/${this.client.id}/settings/deferred-comp-plans`
            this.$api.post(url, data).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showInfo(`Successfully added ${resp.length} plan(s).`)
                this.$emit('updated')
                this.close()
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.formErrors = (errors.__all__ || [])
                this.$bus.showError(errors.__all__)
            })
        },
        getPositions() {
            this.$api.get(`/clients/${this.client.id}/positions`).then(resp => {
                this.allPositions = resp
                this.allPositions.sort((a, b) => {return a.terminal_value.toLowerCase() > b.terminal_value.toLowerCase() ? 1 : -1})
                this.positionsLoaded = true
            }).catch((errors) => {
                this.$bus.showError(errors.__all__)
            })
        },
    }
}
</script>
